import React from "react";
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import Button, { ButtonPurpose } from "shared/Button";
import AlertNotice from "shared/AlertNotice";
import { products as productsApi, errorMessage } from "shared/api";
import styles from "./index.module.css";
import PanelHeader from "../components/PanelHeader";
import { ALL_MEMBERSHIP_CODES, ALL_PIN_CODES } from "./productListItem";
import ProductCatalogBrowser from "./components/ProductCatalog/ProductCatalogBrowser";
import StoreItemActions from "./components/ProductCatalog/StoreItemActions";
import StoreProductEditor from "./ProductEditor";
import AddProductModal from "./components/AddProductModal";
import { Modal } from "shared/Modal";

const STORE_INITIAL_FILTERS = {
  status: ["Published", "Scheduled", "In Review"],
  // Membership / pin-level are inclusion filters that default to all-on so the
  // catalog shows everything until the user narrows it down.
  membership: ALL_MEMBERSHIP_CODES,
  pinLevel: ALL_PIN_CODES,
};

// Collapses "/store/home/" to "/store/home" while leaving a bare "/" alone.
const stripTrailingSlash = (value) =>
  value.length > 1 && value.endsWith("/") ? value.slice(0, -1) : value;

export default function Store() {
  const history = useHistory();
  const { path: matchedPath, url: matchedUrl } = useRouteMatch();
  const location = useLocation();
  // useRouteMatch keeps whatever trailing slash the address bar had, so landing
  // on "/store/home/" would build "/store/home//<id>/edit". The :productId route
  // can't match an empty segment, so that URL renders a blank page.
  const path = stripTrailingSlash(matchedPath);
  const url = stripTrailingSlash(matchedUrl);
  const [addOpen, setAddOpen] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [editingName, setEditingName] = React.useState("");
  const [error, setError] = React.useState(null);
  // Product pending archive confirmation (null = no modal). Guards against
  // accidentally hitting Archive when aiming for Edit, which would instantly
  // hide the item among archived products.
  const [archiveTarget, setArchiveTarget] = React.useState(null);
  // Product pending duplicate confirmation (null = no modal). The copy is
  // written immediately on confirm, so guard the silent click.
  const [duplicateTarget, setDuplicateTarget] = React.useState(null);
  const listScrollRef = React.useRef(0);
  const prevPathRef = React.useRef(location.pathname);

  // Heal links that already went out with a doubled slash (they render blank,
  // since :productId cannot match an empty segment). Preserves the query so a
  // shared filtered URL survives the rewrite.
  React.useEffect(() => {
    if (!location.pathname.includes("//")) return;
    history.replace({
      ...location,
      pathname: location.pathname.replace(/\/{2,}/g, "/"),
    });
  }, [history, location]);

  const isAddPage = location.pathname.endsWith("/add");
  const isEditPage = location.pathname.includes("/edit");
  const isListView = !isAddPage && !isEditPage;

  const editMatch = location.pathname.match(/\/([^/]+)\/edit/);
  const editingProductId = editMatch ? editMatch[1] : null;

  const getScroller = React.useCallback(() => {
    const el = document.getElementById("scrollableDiv");
    if (el && el.scrollHeight > el.clientHeight) return el;
    return null;
  }, []);

  const saveListScroll = React.useCallback(() => {
    const el = getScroller();
    listScrollRef.current = el ? el.scrollTop : window.scrollY;
  }, [getScroller]);

  React.useLayoutEffect(() => {
    const curPath = location.pathname;
    const prevPath = prevPathRef.current;
    const isList = !curPath.includes("/edit") && !curPath.endsWith("/add");
    const wasList = !prevPath.includes("/edit") && !prevPath.endsWith("/add");
    const el = getScroller();
    const setScroll = (y) => {
      if (el) el.scrollTop = y;
      else window.scrollTo(0, y);
    };

    if (!isList) {
      setScroll(0);
    } else if (isList && !wasList) {
      // Returning to the list: restore the saved scroll position and refetch so
      // edits made in the editor show up without a manual refresh. The browser
      // keeps its own filter/sort/page state across the round-trip.
      setScroll(listScrollRef.current);
      setRefreshKey((k) => k + 1);
    }

    prevPathRef.current = curPath;
  }, [location.pathname, getScroller]);

  // Look up the edited product's name for the page title.
  React.useEffect(() => {
    if (!editingProductId) {
      setEditingName("");
      return undefined;
    }
    let cancelled = false;
    productsApi
      .get(editingProductId)
      .then((p) => {
        if (!cancelled) setEditingName(p?.name || "");
      })
      .catch(() => {
        if (!cancelled) setEditingName("");
      });
    return () => {
      cancelled = true;
    };
  }, [editingProductId]);

  const pageTitle = isAddPage
    ? "WWG Store - New Product"
    : isEditPage
    ? `WWG Store - ${editingName || "Edit Product"}`
    : "WWG Store - Home";

  const handleOpenAdd = () => setAddOpen(true);
  const handleCloseAdd = () => setAddOpen(false);

  const handleProductCreated = (product) => {
    setAddOpen(false);
    history.push(`${url}/${product.id}/edit`);
  };

  const handleEditProduct = (product) => {
    saveListScroll();
    history.push(`${url}/${product.id}/edit`);
  };

  const handleDuplicate = (product) => {
    setDuplicateTarget(product);
  };

  const confirmDuplicate = () => {
    if (!duplicateTarget) return;
    const product = duplicateTarget;
    setDuplicateTarget(null);
    saveListScroll();
    productsApi
      .duplicate(product.id)
      .then((created) => {
        if (!created || !created.id) {
          setError(
            "Couldn't create a copy. The original product may no longer be available."
          );
          return;
        }
        history.push(`${url}/${created.id}/edit`);
      })
      .catch((err) => {
        setError(errorMessage(err, "Couldn't duplicate the product."));
      });
  };

  const handleArchive = (product) => {
    setArchiveTarget(product);
  };

  const confirmArchive = () => {
    if (!archiveTarget) return;
    const product = archiveTarget;
    setArchiveTarget(null);
    productsApi
      .archive(product.id)
      .then(() => setRefreshKey((k) => k + 1))
      .catch((err) =>
        setError(errorMessage(err, "Couldn't archive the product."))
      );
  };

  // Restoring an archived product happens through the catalog's batch "Set
  // Status" control, which covers Published/In Review/Archived in one place —
  // the per-row Unarchive button was a second door to the same thing.

  const renderItemActions = React.useCallback(
    (product) => (
      <StoreItemActions
        product={product}
        onEdit={handleEditProduct}
        onArchive={handleArchive}
        onDuplicate={handleDuplicate}
      />
    ),
    // handlers are stable for our purposes; the catalog re-renders on its own
    // state changes regardless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className={styles.container}>
      <PanelHeader title={pageTitle} />

      {error && (
        <div className={styles.alertWrap}>
          <AlertNotice title="Something went wrong">{error}</AlertNotice>
        </div>
      )}

      {/* The catalog stays mounted (just hidden on the editor routes) so its
          search / filter / sort / pagination state survives the round-trip. */}
      <div
        className={styles.content}
        style={{ display: isListView ? "block" : "none" }}
      >
        <ProductCatalogBrowser
          actionColWidth="220px"
          searchPlaceholder="Search Products"
          initialFilters={STORE_INITIAL_FILTERS}
          refreshKey={refreshKey}
          renderItemActions={renderItemActions}
          onItemOpen={handleEditProduct}
          toolbarActions={
            <Button
              onClick={handleOpenAdd}
              className={styles.addButton}
              title="Create a new product and open it in the editor."
            >
              Add New Product
            </Button>
          }
        />
      </div>

      <Switch>
        <Route path={`${path}/add`}>
          <div className={styles.content}>
            <StoreProductEditor mode="add" />
          </div>
        </Route>
        <Route
          path={`${path}/:productId/edit`}
          render={({ match }) => (
            <div className={styles.content}>
              {/* key forces a clean remount when navigating directly between
                  two products' editors (e.g. browser Back/Forward), so one
                  product's version tabs/baseline can't leak into another and
                  corrupt the second product's saved drafts. */}
              <StoreProductEditor key={match.params.productId} mode="edit" />
            </div>
          )}
        />
      </Switch>

      <AddProductModal
        isOpen={addOpen}
        onClose={handleCloseAdd}
        onCreated={handleProductCreated}
      />

      <Modal
        isOpen={Boolean(archiveTarget)}
        title="Archive this product?"
        onClose={() => setArchiveTarget(null)}
      >
        <div className={styles.archiveModalBody}>
          <p>
            Archiving{" "}
            <strong>
              {archiveTarget ? archiveTarget.name : "this product"}
            </strong>{" "}
            hides it from the storefront and removes it from active catalog
            filters. To find it again, set the Status filter to Archived, then
            select it and use Set Status to restore it.
          </p>
          <div className={styles.archiveModalActions}>
            <Button
              onClick={() => setArchiveTarget(null)}
              title="Keep this product in the live store and close this dialog."
            >
              Cancel
            </Button>
            <Button
              purpose={ButtonPurpose.delete}
              onClick={confirmArchive}
              title="Archive this product so it no longer appears in the live store. You can restore it later from the Archived status filter."
            >
              Archive
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(duplicateTarget)}
        title="Create a copy of this product?"
        onClose={() => setDuplicateTarget(null)}
      >
        <div className={styles.archiveModalBody}>
          <p>
            This creates a new editable copy of{" "}
            <strong>
              {duplicateTarget ? duplicateTarget.name : "this product"}
            </strong>{" "}
            and opens it in the editor. The copy is added to the catalog right
            away; if you change your mind, delete it with the trash icon at the
            top of the editor.
          </p>
          <div className={styles.archiveModalActions}>
            <Button
              onClick={() => setDuplicateTarget(null)}
              title="Don't create a copy and close this dialog."
            >
              Cancel
            </Button>
            <Button
              purpose={ButtonPurpose.save}
              onClick={confirmDuplicate}
              title="Create an editable copy of this product as a new draft and open it in the editor."
            >
              Create Copy
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
